import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";
import { FacilityRoutingForm } from "../../../features/facility-routing/components/facility-routing-form";
import { FacilityChooser } from "../../../features/facility-pick/facility-chooser";

/**
 * `RPM-036`'s routing screen (`J-225`), with the building's routing History (`RPM-063`, `J-291`).
 *
 * THE ROUTE COMPOSES FEATURES, as `.claude/rules/coding.md` asks: the chooser is `facility-pick`'s,
 * the profile form is `facility-routing`'s and the History tab is `change-history`'s. The tab is
 * rendered only for an account holding `audit: ["read"]`. The server decides again on every request
 * whether this account may read or change the building's routing, or read its history.
 */
function FacilityRoutingScreen() {
  const [facilityId, setFacilityId] = useState("");
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canReadHistory = usePermission({ audit: ["read"] });
  // A nurse lead reads the routing and does not change it (`RPM-092` row 5); the server decides again.
  const canChange = usePermission({ routing: ["update"] });
  return (
    <div className="flex flex-col gap-4 pt-4">
      <FacilityChooser value={facilityId} onChange={setFacilityId} />
      {facilityId === "" ? null : (
        <HistoryTabs
          showHistory={canReadHistory}
          tab={tab}
          onTabChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
          }
          detailsLabel="Routing"
          details={
            <FacilityRoutingForm key={facilityId} facilityId={facilityId} readOnly={!canChange} />
          }
          history={
            <RecordHistory
              key={facilityId}
              entity="facility-routing"
              records={[{ id: facilityId, label: "" }]}
              noun="Building"
              caption={(label) => `Change history of the routing of ${label || "this building"}`}
              columnLabels={{ routing_behavior: "Behaviour", routing_hold_reason: "Hold reason" }}
            />
          }
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/facility-routing")({
  validateSearch: historyTabSearchSchema,
  component: FacilityRoutingScreen,
  staticData: { screenName: "Routing" },
});
